import React, { useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { Film, CalendarDays, Hourglass, MapPin, BadgeDollarSign, Ticket, Layers, ArrowLeft, Edit, XCircle, PlusCircle, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import OrganizerSeatConfiguration from './OrganizerSeatConfiguration'

interface OrganizerEventDetailsProps {
  movieId: number
  initialScreeningId?: number | null
  onBack: () => void
  onNavigate: (page: string) => void
}

type MovieRow = {
  id: number
  title: string
  image_url: string
  description: string
  duration: number
  price: number
  organizer_id: number | null
  accept_booking?: boolean
  booking_start_time?: string | null
  booking_stop_time?: string | null
  booking_stop_enabled?: boolean
  is_canceled?: boolean
}

type ScreeningRow = { id: number; movie_id: number; start_time: string; available_seats: number; location: string; seat_numbering_start?: string; accept_booking?: boolean | null; booking_start_time?: string | null; booking_stop_time?: string | null; booking_stop_enabled?: boolean | null }
type SeatSplitRow = { screening_id: number; silver_total: number; gold_total: number; diamond_total: number; silver_booked: number; gold_booked: number; diamond_booked: number; silver_price?: number; gold_price?: number; diamond_price?: number }
type BookingRow = { id: number; screening_id: number; total_amount: number; status: string; seats?: number }

export default function OrganizerEventDetails({ movieId, initialScreeningId, onBack, onNavigate }: OrganizerEventDetailsProps) {
  const [loading, setLoading] = useState(true)
  const [movie, setMovie] = useState<MovieRow | null>(null)
  const [screenings, setScreenings] = useState<ScreeningRow[]>([])
  const [splits, setSplits] = useState<SeatSplitRow[]>([])
  const [bookings, setBookings] = useState<BookingRow[]>([])
  const [bookedSeatsTotal, setBookedSeatsTotal] = useState(0) // Legacy total
  const [bookedSeatsByScreening, setBookedSeatsByScreening] = useState<Record<number, number>>({}) // Specific counts
  const [bookedSeatIdsByScreening, setBookedSeatIdsByScreening] = useState<Record<number, string[]>>({}) // For map viz
  const [saving, setSaving] = useState(false)
  const [layout, setLayout] = useState<{ rows: number; cols: number; seats: boolean[][]; seat_numbering_start?: string } | null>(null)
  const [layoutsByScreening, setLayoutsByScreening] = useState<Record<number, { rows: number; cols: number; seats: boolean[][]; seat_numbering_start?: string }>>({})
  const [seatMap, setSeatMap] = useState<Record<string, { booked: boolean; user?: string }>>({})
  const [selectedScreeningId, setSelectedScreeningId] = useState<number | null>(null)
  const [editingMovie, setEditingMovie] = useState<{ title: string; description: string; image_url: string } | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editStartTime, setEditStartTime] = useState('')
  const [editDuration, setEditDuration] = useState<number | ''>('')
  const [editLocation, setEditLocation] = useState('')
  const [editPrice, setEditPrice] = useState<number | ''>('')
  const [silverPct, setSilverPct] = useState(0)
  const [goldPct, setGoldPct] = useState(0)
  const [diamondPct, setDiamondPct] = useState(0)
  const [bookingError, setBookingError] = useState('')
  const startPickerRef = useRef<HTMLInputElement>(null)
  const stopPickerRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    ; (async () => {
      setLoading(true)
      try {
        const { data: m } = await supabase.from('movies').select('*').eq('id', movieId).limit(1)
        setMovie((m && m.length ? (m[0] as MovieRow) : null))
        const { data: ss } = await supabase.from('screenings').select('*').eq('movie_id', movieId).order('start_time', { ascending: true })
        const sRows = ((ss || []) as ScreeningRow[])
        setScreenings(sRows)
        if (sRows.length) {
          // Default to initialScreeningId if provided, otherwise latest
          if (!selectedScreeningId) {
            if (initialScreeningId && sRows.some(s => s.id === initialScreeningId)) {
              setSelectedScreeningId(initialScreeningId)
            } else {
              const latest = sRows.reduce((a, b) => new Date(a.start_time) > new Date(b.start_time) ? a : b)
              setSelectedScreeningId(latest.id)
            }
          }
          const ids = sRows.map(s => s.id)
          const { data: sp } = await supabase.from('seatssplitting').select('*').in('screening_id', ids)
          setSplits(((sp || []) as SeatSplitRow[]))
          const { data: b } = await supabase.from('bookings').select('id,screening_id,total_amount,status,user_id,seats').in('screening_id', ids)
          setBookings(((b || []) as BookingRow[]))
          const { data: bsAll } = await supabase.from('booking_seats').select('screening_id, seat_ids, seat_id').in('screening_id', ids)
          const splitsById: Record<number, SeatSplitRow> = {}
          for (const row of (((sp || []) as SeatSplitRow[]))) splitsById[row.screening_id] = row
          const bookingsById: Record<number, BookingRow[]> = {}
          for (const row of ((((b || []) as BookingRow[])))) {
            if (!bookingsById[row.screening_id]) bookingsById[row.screening_id] = []
            bookingsById[row.screening_id].push(row)
          }
          let totalBooked = 0
          const counts: Record<number, number> = {}
          const seatIdsMap: Record<number, string[]> = {}
          for (const sid of ids) {
            const hasLayout = false // compute later after layoutsMap built
            const bsForSid = ((bsAll || []) as any[]).filter(x => Number(x.screening_id) === sid)
            const bookedSeatsFromLayout = bsForSid.reduce((acc, r) => acc + (Array.isArray(r.seat_ids) ? (r.seat_ids as any[]).length : 1), 0)
            const splitRow = splitsById[sid]
            const bookedFromSplit = splitRow ? (splitRow.silver_booked + splitRow.gold_booked + splitRow.diamond_booked) : 0
            const bookedFromBookings = (bookingsById[sid] || []).filter(x => (x.status || '') === 'confirmed').reduce((acc, x) => acc + (Number(x.seats || 0)), 0)
            // prefer layout counts when any seat IDs exist; else split; else bookings.seats
            const bookedForSid = bookedSeatsFromLayout > 0 ? bookedSeatsFromLayout : (splitRow ? bookedFromSplit : bookedFromBookings)
            counts[sid] = bookedForSid
            // track seat IDs for map
            const idsFromBS = bsForSid.flatMap(r => Array.isArray(r.seat_ids) ? r.seat_ids : [])
            seatIdsMap[sid] = idsFromBS as string[]
            totalBooked += bookedForSid
          }
          setBookedSeatsByScreening(counts)
          setBookedSeatIdsByScreening(seatIdsMap)
          setBookedSeatsTotal(totalBooked)
          const { data: layouts } = await supabase.from('seat_layouts').select('*').in('screening_id', ids)
          const layoutsMap: Record<number, { rows: number; cols: number; seats: boolean[][]; seat_numbering_start?: string }> = {}
          if (layouts && layouts.length) {
            // Build a map from screening id to screening data for quick lookup of seat_numbering_start
            const screeningsById: Record<number, ScreeningRow> = {}
            for (const s of sRows) {
              screeningsById[s.id] = s
            }

            for (const l of (layouts as any[])) {
              const correspondingScreening = screeningsById[l.screening_id]
              layoutsMap[l.screening_id] = {
                rows: l.rows as number,
                cols: l.cols as number,
                seats: (l.seats as any) as boolean[][],
                seat_numbering_start: correspondingScreening?.seat_numbering_start
              }
            }
            setLayoutsByScreening(layoutsMap)
            // prepare seat map visualization for latest screening only
            const latest = sRows.reduce((a, b) => new Date(a.start_time) > new Date(b.start_time) ? a : b)
            const latestLayout = layoutsMap[latest.id]
            if (latestLayout) {
              setLayout(latestLayout)
              const { data: bs } = await supabase.from('booking_seats').select('seat_ids').eq('screening_id', latest.id)
              const bookedIds = ((bs || []) as { seat_ids: string[] }[]).flatMap(r => r.seat_ids)
              const map: Record<string, { booked: boolean; user?: string }> = {}
              for (let r = 0; r < latestLayout.rows; r++) {
                const numbering = latestLayout.seat_numbering_start || 'top-left'
                const isBottomStart = numbering.includes('bottom')
                const isRightToLeft = numbering.includes('right')

                const logicalRowIdx = isBottomStart ? (latestLayout.rows - 1 - r) : r
                const rowLabel = rowLabelFromIndex(logicalRowIdx)

                for (let c = 0; c < latestLayout.cols; c++) {
                  if (!latestLayout.seats[r][c]) continue

                  let seatNumber = 0
                  if (isRightToLeft) {
                    for (let i = latestLayout.cols - 1; i > c; i--) {
                      if (latestLayout.seats[r][i]) seatNumber++
                    }
                  } else {
                    for (let i = 0; i < c; i++) {
                      if (latestLayout.seats[r][i]) seatNumber++
                    }
                  }

                  const id = `${rowLabel}${seatNumber + 1}`
                  map[id] = { booked: bookedIds.includes(id) }
                }
              }
              setSeatMap(map)
            }
          } else {
            setLayoutsByScreening({})
            setLayout(null)
            setSeatMap({})
          }
        } else {
          setSplits([])
          setBookings([])
        }
      } catch (err) {
        toast.error('Failed to load event details')
        console.error(err)
      } finally {
        setLoading(false)
      }
    })()
  }, [movieId])

  useEffect(() => {
    if (!screenings.length) return
    const subs: any[] = []
    for (const s of screenings) {
      const chSeats = supabase
        .channel(`booking_seats_${s.id}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'booking_seats', filter: `screening_id=eq.${s.id}` }, async () => {
          try {
            const ids = screenings.map(x => x.id)
            const { data: bsAll } = await supabase.from('booking_seats').select('screening_id, seat_ids, seat_id').in('screening_id', ids)
            const { data: sp } = await supabase.from('seatssplitting').select('*').in('screening_id', ids)
            const { data: b } = await supabase.from('bookings').select('id,screening_id,total_amount,status,user_id,seats').in('screening_id', ids)
            const splitsById: Record<number, SeatSplitRow> = {}
            for (const row of (((sp || []) as SeatSplitRow[]))) splitsById[row.screening_id] = row
            const bookingsById: Record<number, BookingRow[]> = {}
            for (const row of ((((b || []) as BookingRow[])))) {
              if (!bookingsById[row.screening_id]) bookingsById[row.screening_id] = []
              bookingsById[row.screening_id].push(row)
            }
            let totalBooked = 0
            const counts: Record<number, number> = {}
            const seatIdsMap: Record<number, string[]> = {}
            for (const sid of ids) {
              const bsForSid = ((bsAll || []) as any[]).filter(x => Number(x.screening_id) === sid)
              const bookedSeatsFromLayout = bsForSid.reduce((acc, r) => acc + (Array.isArray(r.seat_ids) ? (r.seat_ids as any[]).length : 1), 0)
              const splitRow = splitsById[sid]
              const bookedFromSplit = splitRow ? (splitRow.silver_booked + splitRow.gold_booked + splitRow.diamond_booked) : 0
              const bookedFromBookings = (bookingsById[sid] || []).filter(x => (x.status || '') === 'confirmed').reduce((acc, x) => acc + (Number(x.seats || 0)), 0)
              const bookedForSid = bookedSeatsFromLayout > 0 ? bookedSeatsFromLayout : (splitRow ? bookedFromSplit : bookedFromBookings)
              counts[sid] = bookedForSid
              // track seat IDs for visualization
              const idsFromBS = bsForSid.flatMap(r => Array.isArray(r.seat_ids) ? r.seat_ids : [])
              // also include confirmed bookings seats? No, bookings table doesn't have seat IDs for GA. 
              // For Reserved Seating, booking_seats table is the source of truth.
              seatIdsMap[sid] = idsFromBS as string[]
              totalBooked += bookedForSid
            }
            setBookedSeatsByScreening(counts)
            setBookedSeatIdsByScreening(seatIdsMap)
            setBookedSeatsTotal(totalBooked)
            // refresh latest screening seat map coloring - REMOVED legacy logic, handled by memo now
          } catch { }
        })


        .subscribe()
      const chSplit = supabase
        .channel(`seatssplitting_${s.id}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'seatssplitting', filter: `screening_id=eq.${s.id}` }, async () => {
          // reuse the same recompute path as above
          const ids = screenings.map(x => x.id)
          const { data: bsAll } = await supabase.from('booking_seats').select('screening_id, seat_ids, seat_id').in('screening_id', ids)
          const { data: sp } = await supabase.from('seatssplitting').select('*').in('screening_id', ids)
          const { data: b } = await supabase.from('bookings').select('id,screening_id,total_amount,status,user_id,seats').in('screening_id', ids)
          const splitsById: Record<number, SeatSplitRow> = {}
          for (const row of (((sp || []) as SeatSplitRow[]))) splitsById[row.screening_id] = row
          const bookingsById: Record<number, BookingRow[]> = {}
          for (const row of ((((b || []) as BookingRow[])))) {
            if (!bookingsById[row.screening_id]) bookingsById[row.screening_id] = []
            bookingsById[row.screening_id].push(row)
          }
          let totalBooked = 0
          const counts: Record<number, number> = {}
          for (const sid of ids) {
            const bsForSid = ((bsAll || []) as any[]).filter(x => Number(x.screening_id) === sid)
            const bookedSeatsFromLayout = bsForSid.reduce((acc, r) => acc + (Array.isArray(r.seat_ids) ? (r.seat_ids as any[]).length : 1), 0)
            const splitRow = splitsById[sid]
            const bookedFromSplit = splitRow ? (splitRow.silver_booked + splitRow.gold_booked + splitRow.diamond_booked) : 0
            const bookedFromBookings = (bookingsById[sid] || []).filter(x => (x.status || '') === 'confirmed').reduce((acc, x) => acc + (Number(x.seats || 0)), 0)
            const bookedForSid = bookedSeatsFromLayout > 0 ? bookedSeatsFromLayout : (splitRow ? bookedFromSplit : bookedFromBookings)
            counts[sid] = bookedForSid
            totalBooked += bookedForSid
          }
          setBookedSeatsByScreening(counts)
          setBookedSeatsTotal(totalBooked)
        })
        .subscribe()
      const chBookings = supabase
        .channel(`bookings_${s.id}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'bookings', filter: `screening_id=eq.${s.id}` }, async () => {
          const ids = screenings.map(x => x.id)
          const { data: bsAll } = await supabase.from('booking_seats').select('screening_id, seat_ids, seat_id').in('screening_id', ids)
          const { data: sp } = await supabase.from('seatssplitting').select('*').in('screening_id', ids)
          const { data: b } = await supabase.from('bookings').select('id,screening_id,total_amount,status,user_id,seats').in('screening_id', ids)
          const splitsById: Record<number, SeatSplitRow> = {}
          for (const row of (((sp || []) as SeatSplitRow[]))) splitsById[row.screening_id] = row
          const bookingsById: Record<number, BookingRow[]> = {}
          for (const row of ((((b || []) as BookingRow[])))) {
            if (!bookingsById[row.screening_id]) bookingsById[row.screening_id] = []
            bookingsById[row.screening_id].push(row)
          }
          let totalBooked = 0
          const counts: Record<number, number> = {}
          for (const sid of ids) {
            const bsForSid = ((bsAll || []) as any[]).filter(x => Number(x.screening_id) === sid)
            const bookedSeatsFromLayout = bsForSid.reduce((acc, r) => acc + (Array.isArray(r.seat_ids) ? (r.seat_ids as any[]).length : 1), 0)
            const splitRow = splitsById[sid]
            const bookedFromSplit = splitRow ? (splitRow.silver_booked + splitRow.gold_booked + splitRow.diamond_booked) : 0
            const bookedFromBookings = (bookingsById[sid] || []).filter(x => (x.status || '') === 'confirmed').reduce((acc, x) => acc + (Number(x.seats || 0)), 0)
            const bookedForSid = bookedSeatsFromLayout > 0 ? bookedSeatsFromLayout : (splitRow ? bookedFromSplit : bookedFromBookings)
            counts[sid] = bookedForSid
            totalBooked += bookedForSid
          }
          setBookedSeatsByScreening(counts)
          setBookedSeatsTotal(totalBooked)
        })
        .subscribe()
      subs.push(chSeats, chSplit, chBookings)
    }
    return () => { for (const ch of subs) supabase.removeChannel(ch) }
  }, [screenings, layoutsByScreening])

  const latestScreening = useMemo(() => {
    if (!screenings.length) return null
    return screenings.reduce((a, b) => new Date(a.start_time) > new Date(b.start_time) ? a : b)
  }, [screenings])
  const firstScreening = useMemo(() => {
    if (!screenings.length) return null
    return screenings.reduce((a, b) => new Date(a.start_time) < new Date(b.start_time) ? a : b)
  }, [screenings])

  const totalAvailableSeats = useMemo(() => screenings.reduce((acc, s) => acc + (s.available_seats || 0), 0), [screenings])
  const selectedTotals = useMemo(() => {
    // If no screening selected, empty
    if (!selectedScreeningId) return { silver: 0, gold: 0, diamond: 0, sum: 0, silverPct: 0, goldPct: 0, diamondPct: 0 }
    const r = splits.find(s => s.screening_id === selectedScreeningId)
    if (!r) return { silver: 0, gold: 0, diamond: 0, sum: 0, silverPct: 0, goldPct: 0, diamondPct: 0 }

    const silver = r.silver_total || 0
    const gold = r.gold_total || 0
    const diamond = r.diamond_total || 0
    const sum = silver + gold + diamond
    const pct = (v: number) => (sum ? Math.round((v / sum) * 100) : 0)
    return { silver, gold, diamond, sum, silverPct: pct(silver), goldPct: pct(gold), diamondPct: pct(diamond) }
  }, [splits, selectedScreeningId])

  // Update local pct state when selected screening (and its totals) changes
  useEffect(() => {
    setSilverPct(selectedTotals.silverPct)
    setGoldPct(selectedTotals.goldPct)
    setDiamondPct(selectedTotals.diamondPct)
  }, [selectedTotals])
  const selectedScreening = useMemo(() => screenings.find(s => s.id === selectedScreeningId) || null, [screenings, selectedScreeningId])

  const bookingsForSelected = useMemo(() => bookings.filter(b => b.screening_id === selectedScreeningId), [bookings, selectedScreeningId])
  const confirmedBookings = useMemo(() => bookingsForSelected.filter(b => (b.status || '') === 'confirmed'), [bookingsForSelected])
  const bookingsCount = useMemo(() => confirmedBookings.length, [confirmedBookings])
  const totalPayments = useMemo(() => confirmedBookings.reduce((acc, b) => acc + Number(b.total_amount || 0), 0), [confirmedBookings])

  const selectedLayout = useMemo(() => selectedScreeningId ? layoutsByScreening[selectedScreeningId] : null, [layoutsByScreening, selectedScreeningId])
  const selectedSplit = useMemo(() => splits.find(s => s.screening_id === selectedScreeningId), [splits, selectedScreeningId])

  // Recalculate booked seats for JUST the selected screening
  const bookedSeatsForSelected = useMemo(() => {
    if (!selectedScreeningId) return 0
    return bookedSeatsByScreening[selectedScreeningId] || 0
  }, [selectedScreeningId, bookedSeatsByScreening])

  const selectedSeatMap = useMemo(() => {
    if (!selectedScreeningId || !selectedLayout) return {}
    // Build map
    const layout = selectedLayout
    const bookedIds = bookedSeatIdsByScreening[selectedScreeningId] || []

    const map: Record<string, { booked: boolean; user?: string }> = {}
    for (let r = 0; r < layout.rows; r++) {
      const numbering = layout.seat_numbering_start || 'top-left'
      const isBottomStart = numbering.includes('bottom')
      const isRightToLeft = numbering.includes('right')

      const logicalRowIdx = isBottomStart ? (layout.rows - 1 - r) : r
      const rowLabel = rowLabelFromIndex(logicalRowIdx)

      for (let c = 0; c < layout.cols; c++) {
        if (!layout.seats[r][c]) continue
        let seatNumber = 0
        if (isRightToLeft) {
          for (let i = layout.cols - 1; i > c; i--) { if (layout.seats[r][i]) seatNumber++ }
        } else {
          for (let i = 0; i < c; i++) { if (layout.seats[r][i]) seatNumber++ }
        }

        const id = `${rowLabel}${seatNumber + 1}`
        map[id] = { booked: bookedIds.includes(id) }
      }
    }
    return map
  }, [selectedLayout, selectedScreeningId, bookedSeatIdsByScreening])

  const totalCapacity = useMemo(() => {
    if (!selectedScreening) return 0
    if (selectedLayout) {
      let count = 0
      for (let r = 0; r < selectedLayout.rows; r++) {
        for (let c = 0; c < selectedLayout.cols; c++) {
          if (selectedLayout.seats[r][c]) count++
        }
      }
      return count
    }
    if (selectedSplit && selectedSplit.silver_total) return (selectedSplit.silver_total + selectedSplit.gold_total + selectedSplit.diamond_total)
    return selectedScreening.available_seats || 0
  }, [selectedScreening, selectedLayout, selectedSplit])

  const availableTotal = useMemo(() => Math.max(0, totalCapacity - bookedSeatsTotal), [totalCapacity, bookedSeatsTotal])

  const updateBookingControls = async (patch: Partial<ScreeningRow>) => {
    if (!latestScreening) return
    setSaving(true)
    // validate dates
    const currentScreening = screenings.find(s => s.id === latestScreening.id)
    if (patch.booking_start_time && patch.booking_stop_time) {
      const start = new Date(patch.booking_start_time)
      const stop = new Date(patch.booking_stop_time)
      if (stop < start) { setBookingError('Stop must be after Start'); setSaving(false); return }
    }
    if (patch.booking_stop_enabled && !(patch.booking_stop_time || currentScreening?.booking_stop_time)) {
      setBookingError('Set a stop date when Stop Booking is enabled'); setSaving(false); return
    }
    if (patch.booking_stop_time && latestScreening.start_time) {
      const stop = new Date(patch.booking_stop_time)
      const eventStart = new Date(latestScreening.start_time)
      if (stop > eventStart) { setBookingError('Stop date must be before event start'); setSaving(false); return }
    }
    setBookingError('')
    const { error } = await supabase.from('screenings').update(patch).eq('id', latestScreening.id)
    if (!error) {
      // Update local screenings state
      setScreenings(prev => prev.map(s => s.id === latestScreening.id ? { ...s, ...patch } : s))
    }
    setSaving(false)
  }

  const saveMovieBasics = async () => {
    if (!movie || !editingMovie) return
    const { error } = await supabase.from('movies').update(editingMovie).eq('id', movie.id)
    if (!error) {
      setMovie({ ...movie, ...editingMovie })
      toast.success('Saved!')
    } else {
      toast.error('Failed to save details')
    }
  }

  const saveTierPricingAndAllocation = async () => {
    // Use the currently-selected screening, not always the latest
    const target = selectedScreeningId
    if (!target) return
    const row = splits.find(s => s.screening_id === target)
    if (!row) return
    await supabase.from('seatssplitting').update({
      silver_total: row.silver_total,
      gold_total: row.gold_total,
      diamond_total: row.diamond_total,
      // Use null (not 0) for unset prices — BookingModal falls back to movie.price on null/undefined
      silver_price:  (row.silver_price  && row.silver_price  > 0) ? row.silver_price  : null,
      gold_price:    (row.gold_price    && row.gold_price    > 0) ? row.gold_price    : null,
      diamond_price: (row.diamond_price && row.diamond_price > 0) ? row.diamond_price : null,
    }).eq('screening_id', target)
  }

  const saveEditEvent = async () => {
    if (editDuration === '' || Number(editDuration) <= 0) { toast.error('Check duration'); return }
    if (!editStartTime) { toast.error('Start time required'); return }
    if (editLocation === '') { toast.error('Location required'); return }
    if (editPrice === '' || Number(editPrice) <= 0) { toast.error('Check price'); return }

    const iso = new Date(editStartTime).toISOString()
    const now = new Date()
    if (new Date(iso) < now) { toast.error('Start time cannot be in the past'); return }

    const dur = Number(editDuration)

    if (!latestScreening) {
      if (!movie) { toast.error('Missing movie data'); return }
      // Create new screening
      const { data: newScr, error: createErr } = await supabase.from('screenings').insert({
        movie_id: movie.id,
        start_time: iso,
        location: editLocation,
        duration: dur,
        available_seats: 100, // Default for new
        enable_seatsplitting: true // Default
      }).select().single()

      if (createErr || !newScr) {
        toast.error('Failed to create screening')
        return
      }

      // Init split
      await supabase.from('seatssplitting').insert({
        screening_id: newScr.id,
        silver_total: 60, silver_booked: 0,
        gold_total: 30, gold_booked: 0,
        diamond_total: 10, diamond_booked: 0
      })

      // Add to local state
      setScreenings(s => [...s, newScr as unknown as ScreeningRow])
      toast.success('Event details saved (New screening created)')
    } else {
      const { error: scrErr } = await supabase.from('screenings').update({ start_time: iso, location: editLocation, duration: dur }).eq('id', latestScreening.id)

      if (scrErr) {
        toast.error('Failed to update screening')
        return
      }
      setScreenings(s => s.map(row => row.id === latestScreening.id ? { ...row, start_time: iso, location: editLocation, duration: dur } : row))
      toast.success('Event details saved!')
    }

    if (movie) {
      if (dur > 0) {
        await supabase.from('movies').update({ duration: dur }).eq('id', movie.id)
        setMovie({ ...movie, duration: dur })
      }
      const pr = Number(editPrice)
      if (pr > 0) {
        await supabase.from('movies').update({ price: pr }).eq('id', movie.id)
        setMovie({ ...movie, price: pr })
      }
    }

    setShowEditModal(false)
  }




  // Auto-close booking if event passed
  useEffect(() => {
    if (!latestScreening) return
    const now = new Date()
    const eventPassed = new Date(latestScreening.start_time) < now
    if (eventPassed && latestScreening.accept_booking) {
      updateBookingControls({ accept_booking: false })
      toast.success('Event ended. Booking closed automatically.')
    }
  }, [latestScreening?.id, latestScreening?.start_time, latestScreening?.accept_booking])

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center h-full min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 text-indigo-600 animate-spin" />
          <p className="text-slate-500 dark:text-slate-400">Loading details...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 p-8">
      <div className="w-full max-w-7xl mx-auto">
        <header className="flex flex-wrap justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="flex items-center justify-center size-10 rounded-full hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors">
              <ArrowLeft className="h-5 w-5 text-slate-600 dark:text-slate-300" />
            </button>
            <div>
              <h1 className="text-slate-900 dark:text-slate-50 text-3xl font-bold leading-tight tracking-tight">Movie Event Details</h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">{movie?.title || ''}</p>
              {screenings.length > 1 && (
                <div className="mt-2">
                  <select
                    value={selectedScreeningId || ''}
                    onChange={(e) => setSelectedScreeningId(Number(e.target.value))}
                    className="text-sm bg-slate-100 dark:bg-slate-800 border-none rounded px-2 py-1 focus:ring-1 focus:ring-indigo-500 text-slate-700 dark:text-slate-200"
                  >
                    {screenings.map(s => (
                      <option key={s.id} value={s.id}>
                        {new Date(s.start_time).toLocaleString()} - {s.location}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {screenings.length === 0 ? (
              <button
                onClick={() => onNavigate('add-screening')}
                className="flex min-w-[84px] items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-indigo-600 text-white text-sm font-bold gap-2 hover:bg-indigo-700 transition-colors"
              >
                <PlusCircle className="h-4 w-4" />
                <span className="truncate">Add Screening</span>
              </button>
            ) : (
              <button
                disabled={!!latestScreening && new Date(latestScreening.start_time) < new Date()}
                onClick={() => {
                  setShowEditModal(true)
                  setEditStartTime(latestScreening ? new Date(latestScreening.start_time).toISOString().slice(0, 16) : '')
                  setEditDuration(movie?.duration ?? 0)
                  setEditLocation(latestScreening?.location || '')
                  setEditPrice(movie?.price ?? 0)
                }}
                className={`flex min-w-[84px] items-center justify-center overflow-hidden rounded-lg h-10 px-4 text-sm font-bold gap-2 ${(!!latestScreening && new Date(latestScreening.start_time) < new Date())
                  ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
                  : 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200'
                  }`}
              >
                <Edit className="h-4 w-4" />
                <span className="truncate">Edit Event</span>
              </button>
            )}
            <button
              disabled={!!latestScreening && new Date(latestScreening.start_time) < new Date()}
              onClick={async () => {
                if (!movie) return
                const ok = window.confirm('Cancelling the event cannot be undone.')
                if (!ok) return
                await supabase.from('movies').update({ is_canceled: true }).eq('id', movie.id)
                setMovie({ ...movie, /* @ts-ignore */ is_canceled: true })
                try {
                  await supabase.functions.invoke('refund-bookings-for-movie', { body: { movie_id: movie.id, fee_percent: 8 } })
                } catch { }
              }}
              className={`flex min-w-[84px] items-center justify-center overflow-hidden rounded-lg h-10 px-4 text-sm font-bold gap-2 ${(!!latestScreening && new Date(latestScreening.start_time) < new Date())
                ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
                : 'bg-red-500/10 text-red-500'
                }`}
            >
              <XCircle className="h-4 w-4" />
              <span className="truncate">Cancel Event</span>
            </button>
            <button onClick={async () => {
              if (!movie) return
              try {
                const { data: screenings } = await supabase.from('screenings').select('id').eq('movie_id', movie.id)
                const ids = ((screenings || []) as { id: number }[]).map(r => r.id)
                if (!ids.length) return
                const { data: bookings } = await supabase.from('bookings').select('id, payment_status').in('screening_id', ids)
                const targetIds = ((bookings || []) as { id: number; payment_status?: string }[]).filter(b => (b.payment_status || '') === 'requires_capture').map(b => b.id)
                for (const bid of targetIds) {
                  await supabase.functions.invoke('capture-payment-intent', { body: { booking_id: bid } })
                }
              } catch { }
            }} className="flex min-w-[84px] items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-sm font-bold gap-2">
              <BadgeDollarSign className="h-4 w-4" />
              <span className="truncate">Run Captures</span>
            </button>
          </div>
        </header>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 flex flex-col gap-6 min-w-0">
            <div className="bg-white dark:bg-slate-900/70 rounded-xl shadow-sm p-6 overflow-hidden">
              {movie?.image_url && (
                <img
                  alt="Movie Poster"
                  className="w-full h-48 sm:h-64 lg:h-80 object-cover rounded-lg mb-6"
                  src={editingMovie?.image_url ?? movie.image_url}
                />
              )}
              <div className="flex flex-col gap-6">
                <div>
                  <h2 className="text-slate-500 dark:text-slate-400 text-sm font-medium">Title</h2>
                  <input
                    value={editingMovie?.title ?? movie?.title ?? ''}
                    onChange={e => setEditingMovie({ ...(editingMovie ?? { title: movie?.title ?? '', description: movie?.description ?? '', image_url: movie?.image_url ?? '' }), title: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <h2 className="text-slate-500 dark:text-slate-400 text-sm font-medium">Description</h2>
                  <textarea
                    value={editingMovie?.description ?? movie?.description ?? ''}
                    onChange={e => setEditingMovie({ ...(editingMovie ?? { title: movie?.title ?? '', description: movie?.description ?? '', image_url: movie?.image_url ?? '' }), description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <h2 className="text-slate-500 dark:text-slate-400 text-sm font-medium">Image URL</h2>
                  <input
                    value={editingMovie?.image_url ?? movie?.image_url ?? ''}
                    onChange={e => setEditingMovie({ ...(editingMovie ?? { title: movie?.title ?? '', description: movie?.description ?? '', image_url: movie?.image_url ?? '' }), image_url: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div className="flex gap-3">
                  <button onClick={saveMovieBasics} className="px-4 py-2 bg-indigo-600 text-white rounded-lg">Save Details</button>
                </div>
              </div>
            </div>

            {/* If no screenings, show specific empty state prompt */}
            {!screenings.length ? (
              <div className="bg-white dark:bg-slate-900/70 rounded-xl shadow-sm p-8 text-center border-2 border-dashed border-slate-300 dark:border-slate-700">
                <CalendarDays className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h2 className="text-slate-900 dark:text-slate-100 text-xl font-bold mb-2">No Screenings Scheduled</h2>
                <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
                  This event is currently not scheduled. Add a screening time and location to enable seat configuration and user bookings.
                </p>
                <button
                  onClick={() => {
                    setShowEditModal(true)
                    setEditStartTime('')
                    setEditDuration(movie?.duration ?? 0)
                    setEditLocation('')
                    setEditPrice(movie?.price ?? 0)
                  }}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors inline-flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Schedule First Screening
                </button>
                <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800">
                  <p className="text-slate-400 dark:text-slate-500 text-sm mb-4">
                    Or if this movie was created by mistake:
                  </p>
                  <button
                    onClick={async () => {
                      if (!movie) return
                      if (window.confirm("Are you sure you want to permanently delete this unscheduled movie?")) {
                        const { error } = await supabase.from('movies').delete().eq('id', movie.id)
                        if (error) {
                          toast.error("Failed to delete movie")
                        } else {
                          toast.success("Movie deleted")
                          onBack()
                        }
                      }
                    }}
                    className="px-4 py-2 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-2"
                  >
                    <XCircle className="h-4 w-4" />
                    Delete Movie
                  </button>
                </div>
              </div>
            ) : (
              <OrganizerSeatConfiguration
                splits={splits}
                setSplits={setSplits}
                latestScreening={selectedScreening as any}
                totals={selectedTotals}
                silverPct={silverPct}
                setSilverPct={setSilverPct}
                goldPct={goldPct}
                setGoldPct={setGoldPct}
                diamondPct={diamondPct}
                setDiamondPct={setDiamondPct}
                layout={selectedLayout}
                seatMap={selectedSeatMap}
                onSaveTierPricing={saveTierPricingAndAllocation}
              />
            )}
          </div>

          {/* Only show Right Column if screenings exist */}
          {screenings.length > 0 && (
            <div className="lg:col-span-1 bg-white dark:bg-slate-900/70 rounded-xl shadow-sm p-6 h-fit min-w-0">
              <h2 className="text-slate-900 dark:text-slate-100 text-xl font-bold mb-6">Event Info</h2>
              <div className="space-y-5">
                {/* Auto-close booking if event passed - Logic moved to top level useEffect */}


                {/* Simplified Booking Status UI */}
                <div className="space-y-3">
                  <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Booking Status</p>
                  <select
                    value={
                      (latestScreening && new Date(latestScreening.start_time) < new Date())
                        ? 'closed'
                        : !latestScreening?.accept_booking
                          ? 'closed'
                          : (latestScreening?.booking_start_time || latestScreening?.booking_stop_enabled)
                            ? 'scheduled'
                            : 'open_now'
                    }
                    onChange={e => {
                      const val = e.target.value
                      const now = new Date()
                      const eventPassed = latestScreening && new Date(latestScreening.start_time) < now

                      if ((val === 'open_now' || val === 'scheduled') && eventPassed) {
                        toast.error('Event hosting date has passed. You cannot reactivate it.')
                        return
                      }

                      if (val === 'open_now') {
                        updateBookingControls({
                          accept_booking: true,
                          booking_start_time: null,
                          booking_stop_enabled: false,
                          booking_stop_time: null
                        })
                      } else if (val === 'scheduled') {
                        // Set default times: opens now, closes at event start
                        const defaultStop = firstScreening?.start_time
                          ? new Date(firstScreening.start_time).toISOString()
                          : null
                        updateBookingControls({
                          accept_booking: true,
                          booking_start_time: new Date().toISOString(),
                          booking_stop_enabled: true,
                          booking_stop_time: defaultStop
                        })
                      } else if (val === 'closed') {
                        updateBookingControls({ accept_booking: false })
                      }
                    }}
                    disabled={saving}
                    className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="open_now">🟢 Open Now</option>
                    <option value="scheduled">📅 Scheduled</option>
                    <option value="closed">🔴 Closed</option>
                  </select>
                  {/* Helper text */}
                  <p className="text-xs text-slate-400 dark:text-slate-500">
                    {!latestScreening?.accept_booking
                      ? 'Booking is disabled. Users cannot book tickets.'
                      : (!latestScreening?.booking_start_time && !latestScreening?.booking_stop_enabled)
                        ? 'Booking is open. Users can book now until the event.'
                        : 'Booking follows the scheduled times below.'}
                  </p>
                </div>
                {/* Conditional Opens At / Closes At for Scheduled mode */}
                {latestScreening?.accept_booking && (latestScreening?.booking_start_time || latestScreening?.booking_stop_enabled) && (
                  <div className="space-y-3 pt-2">
                    <div>
                      <label className="block text-slate-500 dark:text-slate-400 text-sm font-medium mb-1">Opens At</label>
                      <input
                        ref={startPickerRef}
                        type="datetime-local"
                        value={latestScreening?.booking_start_time ? new Date(latestScreening.booking_start_time).toISOString().slice(0, 16) : ''}
                        onChange={e => updateBookingControls({ booking_start_time: e.target.value ? new Date(e.target.value).toISOString() : null })}
                        disabled={saving}
                        className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-500 dark:text-slate-400 text-sm font-medium mb-1">Closes At</label>
                      <input
                        ref={stopPickerRef}
                        type="datetime-local"
                        value={latestScreening?.booking_stop_time ? new Date(latestScreening.booking_stop_time).toISOString().slice(0, 16) : ''}
                        onChange={e => updateBookingControls({ booking_stop_time: e.target.value ? new Date(e.target.value).toISOString() : null, booking_stop_enabled: !!e.target.value })}
                        max={firstScreening ? new Date(firstScreening.start_time).toISOString().slice(0, 16) : undefined}
                        disabled={saving}
                        className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Must be before event starts{firstScreening ? ` (${new Date(firstScreening.start_time).toLocaleString()})` : ''}.</p>
                    </div>
                  </div>
                )}
                {bookingError && <p className="text-xs text-red-400">{bookingError}</p>}
                <hr className="border-slate-200 dark:border-slate-800 my-2" />
                <div className="flex items-start gap-4">
                  <Film className="text-indigo-600 dark:text-indigo-400 h-5 w-5 mt-0.5" />
                  <div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-1">Event Type</p>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300">
                      Movie
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CalendarDays className="text-indigo-600 dark:text-indigo-400 h-5 w-5 mt-0.5" />
                  <div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Start Time</p>
                    <p className="text-slate-900 dark:text-slate-100 text-base">{selectedScreening ? new Date(selectedScreening.start_time).toLocaleString() : '—'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Hourglass className="text-indigo-600 dark:text-indigo-400 h-5 w-5 mt-0.5" />
                  <div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Duration</p>
                    <p className="text-slate-900 dark:text-slate-100 text-base">{movie?.duration || 0} minutes</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <MapPin className="text-indigo-600 dark:text-indigo-400 h-5 w-5 mt-0.5" />
                  <div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Location</p>
                    <p className="text-slate-900 dark:text-slate-100 text-base">{selectedScreening?.location || '—'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <BadgeDollarSign className="text-indigo-600 dark:text-indigo-400 h-5 w-5 mt-0.5" />
                  <div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Price</p>
                    <p className="text-slate-900 dark:text-slate-100 text-base">€{movie?.price || 0}</p>
                  </div>
                </div>
                <hr className="border-slate-200 dark:border-slate-800 my-2" />
                <div className="flex items-start gap-4">
                  <Ticket className="text-indigo-600 dark:text-indigo-400 h-5 w-5 mt-0.5" />
                  <div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Bookings</p>
                    <p className="text-slate-900 dark:text-slate-100 text-base">{bookedSeatsForSelected} / {totalCapacity}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Layers className="text-indigo-600 dark:text-indigo-400 h-5 w-5 mt-0.5" />
                  <div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Total Available Seats</p>
                    <p className="text-slate-900 dark:text-slate-100 text-base">{availableTotal}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <BadgeDollarSign className="text-indigo-600 dark:text-indigo-400 h-5 w-5 mt-0.5" />
                  <div>
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Total Payments</p>
                    <p className="text-slate-900 dark:text-slate-100 text-base">€{totalPayments.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {showEditModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
              <h3 className="text-gray-900 dark:text-white text-lg font-semibold mb-4">Edit Event Details</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Screening Time</label>
                  <input
                    type="datetime-local"
                    value={editStartTime}
                    onChange={e => setEditStartTime(e.target.value)}
                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Duration (minutes)</label>
                  <input
                    type="number"
                    min={1}
                    value={editDuration}
                    onChange={e => setEditDuration(Number(e.target.value))}
                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Location</label>
                  <input
                    value={editLocation}
                    onChange={e => setEditLocation(e.target.value)}
                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Price (€)</label>
                  <input
                    type="number"
                    min={0.01}
                    step="0.01"
                    value={editPrice}
                    onChange={e => setEditPrice(Number(e.target.value))}
                    className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowEditModal(false)} className="px-4 py-2 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 rounded-lg">Cancel</button>
                <button onClick={saveEditEvent} className="px-4 py-2 bg-indigo-600 text-white rounded-lg">Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
const rowLabelFromIndex = (idx: number): string => {
  let n = idx
  let s = ''
  while (n >= 0) {
    s = String.fromCharCode(65 + (n % 26)) + s
    n = Math.floor(n / 26) - 1
  }
  return s
}
